import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllUsers, setUserRole, ROLES, assignProjectToUser, removeProjectFromUser, assignContractProjectToUser, removeContractProjectFromUser, generateContractProjectKey } from '../userRoles';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import styles from '../styles/userAdminStyles';

const UserAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateStatus, setUpdateStatus] = useState({ success: false, error: null });
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [contractProjectCombinations, setContractProjectCombinations] = useState([]);

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersData = await getAllUsers();
        setUsers(usersData);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Error al cargar usuarios. Por favor intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);
  
  // Cargar todos los proyectos, productos y generar combinaciones
  useEffect(() => {
    const fetchProjectsAndProducts = async () => {
      try {
        const actasRef = collection(db, 'actas');
        const q = query(actasRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        // Extraer proyectos y productos únicos de las actas
        const uniqueProjects = new Set();
        const uniqueProducts = new Set();
        const combinations = new Set();
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Extraer proyectos
          if (data.tipoProyecto) {
            uniqueProjects.add(data.tipoProyecto);
          }
          if (data.tipoProyectos && Array.isArray(data.tipoProyectos)) {
            data.tipoProyectos.forEach(proyecto => uniqueProjects.add(proyecto));
          }
          
          // Extraer productos
          if (data.productosProyecto && Array.isArray(data.productosProyecto)) {
            data.productosProyecto.forEach(producto => uniqueProducts.add(producto));
          }
          
          // Generar combinaciones contrato/proyecto
          const proyectos = data.tipoProyectos || (data.tipoProyecto ? [data.tipoProyecto] : []);
          const productos = data.productosProyecto || [];
          
          if (proyectos.length > 0 && productos.length > 0) {
            proyectos.forEach(proyecto => {
              productos.forEach(producto => {
                const combinationKey = generateContractProjectKey(proyecto, producto);
                combinations.add(combinationKey);
              });
            });
          } else if (proyectos.length > 0) {
            // Si no hay productos, usar solo el proyecto
            proyectos.forEach(proyecto => {
              const combinationKey = generateContractProjectKey(proyecto, 'Sin producto específico');
              combinations.add(combinationKey);
            });
          }
        });
        
        setProjects(Array.from(uniqueProjects).sort());
        setProducts(Array.from(uniqueProducts).sort());
        setContractProjectCombinations(Array.from(combinations).sort());
      } catch (error) {
        console.error('Error al cargar proyectos y productos:', error);
      }
    };
    
    fetchProjectsAndProducts();
  }, []);

  // Handle role change
  const handleRoleChange = async (userId, newRole) => {
    try {
      setLoading(true);
      const success = await setUserRole(userId, newRole);
      
      if (success) {
        // Update local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, role: newRole } : user
          )
        );
        setUpdateStatus({ success: true, error: null });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setUpdateStatus({ success: false, error: null });
        }, 3000);
      } else {
        throw new Error('No se pudo actualizar el rol del usuario');
      }
    } catch (err) {
      console.error('Error updating user role:', err);
      setUpdateStatus({ success: false, error: 'Error al actualizar el rol. Por favor intente nuevamente.' });
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setUpdateStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la asignación de un proyecto a un usuario (mantener compatibilidad)
  const handleAssignProject = async (userId) => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      const success = await assignProjectToUser(userId, selectedProject);
      
      if (success) {
        // Actualizar el estado local
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === userId) {
              const updatedAllowedProjects = [...(user.allowedProjects || [])]; 
              if (!updatedAllowedProjects.includes(selectedProject)) {
                updatedAllowedProjects.push(selectedProject);
              }
              return { ...user, allowedProjects: updatedAllowedProjects };
            }
            return user;
          })
        );
        setUpdateStatus({ success: true, error: null });
        setSelectedProject('');
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setUpdateStatus({ success: false, error: null });
        }, 3000);
      } else {
        throw new Error('No se pudo asignar el proyecto al usuario');
      }
    } catch (err) {
      console.error('Error asignando proyecto:', err);
      setUpdateStatus({ success: false, error: 'Error al asignar el proyecto. Por favor intente nuevamente.' });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setUpdateStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la asignación de una combinación contrato/proyecto a un usuario
  const handleAssignContractProject = async (userId) => {
    if (!selectedProject || !selectedProduct) return;
    
    try {
      setLoading(true);
      const contractProjectKey = generateContractProjectKey(selectedProject, selectedProduct);
      const success = await assignContractProjectToUser(userId, contractProjectKey);
      
      if (success) {
        // Actualizar el estado local
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === userId) {
              const updatedAllowedContractProjects = [...(user.allowedContractProjects || [])]; 
              if (!updatedAllowedContractProjects.includes(contractProjectKey)) {
                updatedAllowedContractProjects.push(contractProjectKey);
              }
              return { ...user, allowedContractProjects: updatedAllowedContractProjects };
            }
            return user;
          })
        );
        setUpdateStatus({ success: true, error: null });
        setSelectedProject('');
        setSelectedProduct('');
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setUpdateStatus({ success: false, error: null });
        }, 3000);
      } else {
        throw new Error('No se pudo asignar la combinación contrato/proyecto al usuario');
      }
    } catch (err) {
      console.error('Error asignando combinación contrato/proyecto:', err);
      setUpdateStatus({ success: false, error: 'Error al asignar la combinación contrato/proyecto. Por favor intente nuevamente.' });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setUpdateStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la eliminación de un proyecto de un usuario (mantener compatibilidad)
  const handleRemoveProject = async (userId, projectName) => {
    try {
      setLoading(true);
      const success = await removeProjectFromUser(userId, projectName);
      
      if (success) {
        // Actualizar el estado local
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === userId) {
              const updatedAllowedProjects = (user.allowedProjects || []).filter(
                project => project !== projectName
              );
              return { ...user, allowedProjects: updatedAllowedProjects };
            }
            return user;
          })
        );
        setUpdateStatus({ success: true, error: null });
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setUpdateStatus({ success: false, error: null });
        }, 3000);
      } else {
        throw new Error('No se pudo eliminar el proyecto del usuario');
      }
    } catch (err) {
      console.error('Error eliminando proyecto:', err);
      setUpdateStatus({ success: false, error: 'Error al eliminar el proyecto. Por favor intente nuevamente.' });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setUpdateStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la eliminación de una combinación contrato/proyecto de un usuario
  const handleRemoveContractProject = async (userId, contractProjectKey) => {
    try {
      setLoading(true);
      const success = await removeContractProjectFromUser(userId, contractProjectKey);
      
      if (success) {
        // Actualizar el estado local
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === userId) {
              const updatedAllowedContractProjects = (user.allowedContractProjects || []).filter(
                combination => combination !== contractProjectKey
              );
              return { ...user, allowedContractProjects: updatedAllowedContractProjects };
            }
            return user;
          })
        );
        setUpdateStatus({ success: true, error: null });
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setUpdateStatus({ success: false, error: null });
        }, 3000);
      } else {
        throw new Error('No se pudo eliminar la combinación contrato/proyecto del usuario');
      }
    } catch (err) {
      console.error('Error eliminando combinación contrato/proyecto:', err);
      setUpdateStatus({ success: false, error: 'Error al eliminar la combinación contrato/proyecto. Por favor intente nuevamente.' });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setUpdateStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h5" sx={styles.title}>
          Administración de Usuarios
        </Typography>
      </Box>
      
      {updateStatus.success && (
        <Alert severity="success" sx={styles.statusAlert}>
          Rol actualizado correctamente.
        </Alert>
      )}
      
      {updateStatus.error && (
        <Alert severity="error" sx={styles.statusAlert}>
          {updateStatus.error}
        </Alert>
      )}
      
      {loading && users.length === 0 ? (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Email</TableCell>
                <TableCell>Rol Actual</TableCell>
                <TableCell>Cambiar Rol</TableCell>
                <TableCell>Contratos Asignados (Legacy)</TableCell>
                <TableCell>Combinaciones Contrato/Proyecto</TableCell>
                <TableCell>Asignar Combinación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay usuarios registrados
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-block',
                        bgcolor: user.role === ROLES.ADMIN ? '#e3f2fd' : 
                                user.role === ROLES.GESTOR ? '#e8f5e9' : 
                                user.role === ROLES.APROBADOR ? '#f3e5f5' : '#fff3e0',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: user.role === ROLES.ADMIN ? '#1565c0' : 
                               user.role === ROLES.GESTOR ? '#2e7d32' : 
                               user.role === ROLES.APROBADOR ? '#7b1fa2' : '#ef6c00'
                      }}>
                        {user.role === ROLES.ADMIN ? 'Administrador' : 
                         user.role === ROLES.GESTOR ? 'Gestor' : 
                         user.role === ROLES.APROBADOR ? 'Aprobador' : 'Usuario'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id={`role-select-${user.id}`}>Rol</InputLabel>
                        <Select
                          labelId={`role-select-${user.id}`}
                          value={user.role}
                          label="Rol"
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={loading}
                        >
                          <MenuItem value={ROLES.ADMIN}>Administrador</MenuItem>
                          <MenuItem value={ROLES.GESTOR}>Gestor</MenuItem>
                          <MenuItem value={ROLES.APROBADOR}>Aprobador</MenuItem>
                          <MenuItem value={ROLES.USUARIO}>Usuario</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
                        {user.allowedProjects && user.allowedProjects.length > 0 ? (
                          user.allowedProjects.map((project) => (
                            <Chip
                              key={project}
                              label={project}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onDelete={() => handleRemoveProject(user.id, project)}
                              sx={{ m: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sin contratos asignados
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 400 }}>
                        {user.allowedContractProjects && user.allowedContractProjects.length > 0 ? (
                          user.allowedContractProjects.map((combination) => {
                            const [contract, project] = combination.split('|');
                            return (
                              <Chip
                                key={combination}
                                label={`${contract} | ${project}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                onDelete={() => handleRemoveContractProject(user.id, combination)}
                                sx={{ m: 0.5 }}
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sin combinaciones asignadas
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(user.role === ROLES.USUARIO || user.role === ROLES.APROBADOR) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Asignación de combinación contrato/proyecto */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel id={`contract-select-${user.id}`}>Contrato</InputLabel>
                              <Select
                                labelId={`contract-select-${user.id}`}
                                value={user.id === selectedUser ? selectedProject : ''}
                                label="Contrato"
                                onChange={(e) => {
                                  setSelectedProject(e.target.value);
                                  setSelectedUser(user.id);
                                }}
                                disabled={loading}
                              >
                                {projects.map((project) => (
                                  <MenuItem key={project} value={project}>
                                    {project}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel id={`product-select-${user.id}`}>Proyecto</InputLabel>
                              <Select
                                labelId={`product-select-${user.id}`}
                                value={user.id === selectedUser ? selectedProduct : ''}
                                label="Producto"
                                onChange={(e) => {
                                  setSelectedProduct(e.target.value);
                                  setSelectedUser(user.id);
                                }}
                                disabled={loading || !selectedProject}
                              >
                                <MenuItem value="Sin producto específico">
                                  Sin producto específico
                                </MenuItem>
                                {products.map((product) => (
                                  <MenuItem key={product} value={product}>
                                    {product}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Tooltip title="Asignar combinación contrato/proyecto">
                              <IconButton 
                                color="secondary" 
                                onClick={() => handleAssignContractProject(user.id)}
                                disabled={!selectedProject || !selectedProduct || selectedUser !== user.id}
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          
                          {/* Asignación legacy de contrato solo */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Legacy - Solo contrato:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel id={`legacy-project-select-${user.id}`}>Contrato</InputLabel>
                              <Select
                                labelId={`legacy-project-select-${user.id}`}
                                value={''}
                                label="Contrato"
                                onChange={(e) => {
                                  setSelectedProject(e.target.value);
                                  setSelectedUser(user.id);
                                }}
                                disabled={loading}
                              >
                                {projects.map((project) => (
                                  <MenuItem 
                                    key={project} 
                                    value={project}
                                    disabled={user.allowedProjects && user.allowedProjects.includes(project)}
                                  >
                                    {project}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Tooltip title="Asignar contrato (legacy)">
                              <IconButton 
                                color="primary" 
                                onClick={() => handleAssignProject(user.id)}
                                disabled={!selectedProject || selectedUser !== user.id}
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                      {(user.role === ROLES.ADMIN || user.role === ROLES.GESTOR) && (
                        <Typography variant="body2" color="text.secondary">
                          No necesario (tiene acceso a todas las combinaciones)
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default UserAdmin;